import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ProformaInvoiceConfirmationPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    delivery_documents: false,
    inventory_usage: false,
    invoice_payment: false,
    delivery_details: false,
    final_approval: false
  });
  
  // Delivery Confirmation sections
  const confirmationSections = [
    { key: 'delivery_documents', name: 'Documents Page', icon: <DescriptionIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />, hasApprove: false },
    { key: 'inventory_usage', name: 'Inventory Usage Page', icon: <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />, hasApprove: false, hasDelete: false },
    { key: 'invoice_payment', name: 'Invoice & Payment Page', icon: <PaymentIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />, hasApprove: false, hasDelete: false },
    { key: 'delivery_details', name: 'Delivery Details Page', icon: <LocalShippingIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />, hasApprove: false, hasDelete: false },
    { key: 'final_approval', name: 'Final Approval Page', icon: <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />, hasApprove: true, hasEdit: false, hasDelete: false }
  ];

  const renderConfirmationSection = (section) => {
    const isExpanded = expandedSections[section.key] !== false;
    const sectionPermissions = permissions.filter(p => 
      p.name && p.name.includes(`sales.purchase_orders.${section.key}`)
    );
    
    if (sectionPermissions.length === 0) return null;

    const viewPermission = sectionPermissions.find(p => p.name?.includes('.view'));
    const editPermission = sectionPermissions.find(p => p.name?.includes('.edit'));
    const deletePermission = sectionPermissions.find(p => p.name?.includes('.delete'));
    const approvePermission = sectionPermissions.find(p => p.name?.includes('.approve'));

    return (
      <Box 
        key={section.key}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Section Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [section.key]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {section.icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {section.name}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {section.name.toLowerCase()} section
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPermission.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPermission.id]) {
                      newPermissions[viewPermission.id] = {};
                    }
                    newPermissions[viewPermission.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPermission && section.hasEdit !== false && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {section.name.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPermission.id]) {
                      newPermissions[editPermission.id] = {};
                    }
                    newPermissions[editPermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission */}
            {deletePermission && section.hasDelete !== false && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: section.hasApprove && approvePermission ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {section.name.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePermission.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePermission.id]) {
                      newPermissions[deletePermission.id] = {};
                    }
                    newPermissions[deletePermission.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve Permission */}
            {section.hasApprove && approvePermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve {section.name.toLowerCase()} requests
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePermission.id]) {
                      newPermissions[approvePermission.id] = {};
                    }
                    newPermissions[approvePermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {confirmationSections.map(section => renderConfirmationSection(section))}
    </Box>
  );
};

export default ProformaInvoiceConfirmationPermissions;
